import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = logged out, object = user
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = async (email, password) => {
        // Defensive: mobile keyboards often inject trailing spaces or
        // auto-capitalize the first char. Normalize here so callers don't
        // have to worry about it.
        const cleanEmail = (email || "").trim().toLowerCase();
        const cleanPassword = (password || "").trim();
        const { data } = await api.post("/auth/login", { email: cleanEmail, password: cleanPassword });
        setUser(data);
        return data;
    };

    const register = async ({ email, password, role, nom_etablissement, nom, prenom, phone, invite_code, activation_code, niveau }) => {
        const cleanEmail = (email || "").trim().toLowerCase();
        const cleanPassword = (password || "").trim();
        const { data } = await api.post("/auth/register", { email: cleanEmail, password: cleanPassword, role, nom_etablissement, nom, prenom, phone, invite_code, activation_code, niveau });
        setUser(data);
        return data;
    };

    const logout = async () => {
        // Clear local state FIRST so the UI flips immediately even if the
        // backend call is slow.
        setUser(false);
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // ignore — cookie already invalid client-side
        }
        // Force a hard reload to drop any in-memory cached request and ensure
        // the browser re-fetches /auth/me with the cleared cookie.
        window.location.href = "/login";
    };

    // Memo the context value so consumers don't re-render on every parent
    // render. Without this, the inline object is a new reference each pass
    // and every component using useAuth() re-renders even when nothing changed.
    const contextValue = useMemo(
        () => ({ user, loading, login, register, logout, refresh, setUser }),
        [user, loading, refresh]
    );

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
